// Manage — Steward Tools for a single project.
//
// Route: /project/:id/manage
//
// This is deliberately narrow: "the ability to edit the things a
// steward was able to define during project creation." No analytics,
// no participant CRM, no messaging, no arbitrary status controls.
// Create and Steward Tools are two modes of the same underlying
// project-definition system — both are powered by ProjectForm.
//
// Access is gated to project stewards and co-stewards. Non-stewards
// (including anonymous visitors landing on the URL) get bounced to
// the read-only Project Detail page for the same project.

import { Navigate, useParams } from "react-router-dom";
import ProjectForm from "../components/ProjectForm";
import { useCommons } from "../lib/store";
import { projectById, isStewardOrCo } from "../lib/domain";

export default function Manage() {
  const { id } = useParams();
  const { data, user } = useCommons();

  if (!data || !user) return <div className="c-load">Loading…</div>;

  const project = projectById(data.projects, id);
  if (!project) {
    // Unknown project id — send them home rather than to a broken
    // detail page.
    return <Navigate to="/" replace />;
  }

  const allowed = isStewardOrCo(user.personId, project);
  if (!allowed) {
    // Non-stewards should never see Steward Tools. Silent redirect to
    // the read-only detail view for the same project — no toast, no
    // "access denied" scolding; the affordance simply isn't for them.
    return <Navigate to={`/project/${project.id}`} replace />;
  }

  // Same route + id but different re-mount of the sub-tree on each
  // project change is guaranteed by the router — key it explicitly
  // just to make that intent unmistakable if the project id ever
  // changes while this page is mounted.
  return (
    <ProjectForm
      key={project.id}
      mode="edit"
      project={project}
      testIdPrefix="manage"
    />
  );
}
